import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { applyBlockEdit, buildDesignHtml, serializeBlock } from '../lib/emailBlocks';

export interface VisualEmailEditorHandle {
  /** Inserts a variable chip at the cursor in the last-focused text block. */
  insertVariable: (raw: string, label: string) => boolean;
  command: (cmd: 'bold' | 'italic' | 'link' | 'unlink') => void;
}

interface Props {
  html: string;
  sample: Record<string, unknown>;
  /** Bump to rebuild the editing view from `html` (load, discard, reset, source edits). */
  rebuildKey: number;
  onChange: (html: string) => void;
}

/**
 * Edits an email template in place: the email renders as it will look, and its
 * text blocks are directly editable. Edits are spliced back into the template
 * string (see lib/emailBlocks.ts) so the surrounding layout never changes.
 */
export const VisualEmailEditor = forwardRef<VisualEmailEditorHandle, Props>(function VisualEmailEditor(
  { html, sample, rebuildKey, onChange },
  ref
) {
  const frame = useRef<HTMLIFrameElement>(null);
  const htmlRef = useRef(html);
  htmlRef.current = html;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastSeg = useRef<HTMLElement | null>(null);
  const savedRange = useRef<Range | null>(null);

  // Only rebuilt on rebuildKey: re-rendering on every keystroke would drop the cursor.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const srcDoc = useMemo(() => buildDesignHtml(htmlRef.current, sample), [rebuildKey, sample]);

  const doc = () => frame.current?.contentDocument ?? null;

  const fitHeight = () => {
    const d = doc();
    if (d && frame.current) frame.current.style.height = `${d.documentElement.scrollHeight + 4}px`;
  };

  const commit = (seg: HTMLElement) => {
    const index = Number(seg.dataset.seg);
    onChangeRef.current(applyBlockEdit(htmlRef.current, index, serializeBlock(seg)));
    fitHeight();
  };

  // nodeType, not instanceof: nodes in the iframe belong to its own window's Element class.
  const segOf = (node: Node | null) =>
    (node?.nodeType === Node.ELEMENT_NODE ? (node as Element) : node?.parentElement)?.closest<HTMLElement>(
      '[data-seg]'
    ) ?? null;

  const restoreSelection = () => {
    const d = doc();
    const seg = lastSeg.current;
    if (!d || !seg) return false;
    seg.focus();
    const sel = d.getSelection();
    if (sel && savedRange.current && seg.contains(savedRange.current.startContainer)) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    return true;
  };

  const wired = useRef(new WeakSet<Document>());

  // Wires a freshly parsed editing document. Runs as soon as it's parsed rather
  // than on iframe load, which waits for every image (the sample thumbnails).
  const wire = (d: Document) => {
    if (wired.current.has(d)) return;
    wired.current.add(d);
    lastSeg.current = null;
    savedRange.current = null;
    d.addEventListener('input', (e) => {
      const seg = segOf(e.target as Node);
      if (seg) commit(seg);
    });
    d.addEventListener('focusin', (e) => {
      const seg = segOf(e.target as Node);
      if (seg) lastSeg.current = seg;
    });
    d.addEventListener('selectionchange', () => {
      const sel = d.getSelection();
      if (sel && sel.rangeCount && segOf(sel.anchorNode)) savedRange.current = sel.getRangeAt(0).cloneRange();
    });
    d.addEventListener('keydown', (e) => {
      if (!segOf(e.target as Node)) return;
      // A block is one paragraph: Enter adds a line break, not a new block.
      if (e.key === 'Enter') {
        e.preventDefault();
        d.execCommand('insertLineBreak');
      }
    });
    // Pasted text arrives as plain text, never as foreign markup or styles.
    d.addEventListener('paste', (e) => {
      if (!segOf(e.target as Node)) return;
      e.preventDefault();
      d.execCommand('insertText', false, e.clipboardData?.getData('text/plain') ?? '');
    });
    // Links in the editing view shouldn't navigate away.
    d.addEventListener('click', (e) => {
      if ((e.target as Element).closest('a')) e.preventDefault();
    });
    d.querySelectorAll<HTMLElement>('[data-seg]').forEach((seg) => (seg.contentEditable = 'true'));
    fitHeight();
  };

  useEffect(() => {
    const id = window.setInterval(() => {
      const d = doc();
      // Skip the previous document, still in place until the new srcdoc parses.
      if (d && d.URL === 'about:srcdoc' && d.readyState !== 'loading' && !wired.current.has(d)) {
        wire(d);
        window.clearInterval(id);
      }
    }, 30);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcDoc]);

  // Images change the height once they arrive.
  const onLoad = () => {
    const d = doc();
    if (d) wire(d);
    fitHeight();
  };

  useImperativeHandle(ref, () => ({
    insertVariable(raw, label) {
      const d = doc();
      if (!d || !restoreSelection()) return false;
      const chip = d.createElement('span');
      chip.className = '__var';
      chip.contentEditable = 'false';
      chip.dataset.raw = raw;
      chip.textContent = label;
      const sel = d.getSelection();
      const range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
      if (range && lastSeg.current!.contains(range.startContainer)) {
        range.deleteContents();
        range.insertNode(chip);
        range.setStartAfter(chip);
        range.collapse(true);
        sel!.removeAllRanges();
        sel!.addRange(range);
      } else {
        lastSeg.current!.appendChild(chip);
      }
      commit(lastSeg.current!);
      return true;
    },
    command(cmd) {
      const d = doc();
      if (!d || !restoreSelection()) return;
      if (cmd === 'link') {
        const url = window.prompt('Link address (https://…, mailto:…, or a variable like {{statusUrl}})');
        if (!url) return;
        if (!/^(https?:\/\/|mailto:|\{\{)/i.test(url.trim())) {
          window.alert('Links must start with https://, http://, mailto: or a {{variable}}.');
          return;
        }
        restoreSelection();
        d.execCommand('createLink', false, url.trim());
      } else {
        d.execCommand(cmd);
      }
      if (lastSeg.current) commit(lastSeg.current);
    },
  }));

  return (
    <iframe
      ref={frame}
      title="Email editor"
      // Not sandboxed: a sandbox without allow-scripts also stops our own listeners
      // from firing. Template code is blocked by the CSP meta that buildDesignHtml adds.
      srcDoc={srcDoc}
      onLoad={onLoad}
      style={{ width: '100%', minHeight: 480, border: '1px solid #e5e5e5', borderRadius: 6, background: '#fafafa' }}
    />
  );
});
